import { InjectionToken } from "@angular/core";
import { routerReducer, RouterReducerState } from "@ngrx/router-store";
import { ActionReducerMap, MetaReducer } from "@ngrx/store";

import { LogoutRequested } from "../actions/user.actions";
import { ApiClientState } from "../models/api-client-state";
import { BusinessClassificationState } from "../models/business-classification-state";
import { DataPerProductState } from "../models/consumer/data-per-product-state";
import { OrderCostStatsState } from "../models/consumer/order-cost-stats-state";
import { VendorGroupsState } from "../models/consumer/vendor-groups-state";
import { ConsumerRolesState } from "../models/consumer-admin/consumer-roles.state";
import { GetActiveVendorOwnersState } from "../models/consumer-admin/get-active-vendor-owners.state";
import { AchBoStatusState } from "../models/consumer-logs/ach-bo-status-state";
import { AchDocumentState } from "../models/consumer-logs/ach-document-state";
import { AchStatusState } from "../models/consumer-logs/ach-status-state";
import { ConsumerState } from "../models/consumer-state";
import { CountState } from "../models/count-state";
import { FundingBalanceState } from "../models/funding-balance-state";
import { HrrState } from "../models/hrr/hrr.state";
import { ConversationState } from "../models/messaging/conversation-state";
import { MessageState } from "../models/messaging/message-state";
import { AccountRequestsState } from "../models/mp-admin/account-requests-state";
import { ActivityCountState } from "../models/mp-admin/activity-count-state";
import { AdminDailyStatsState } from "../models/mp-admin/admin-daily-stats-state";
import { AdminStatsState } from "../models/mp-admin/admin-stats-state";
import { AnalyticsExclusionState } from "../models/mp-admin/analytics-exclusion-state";
import { BillableProposalsState } from "../models/mp-admin/billable-proposals-state";
import { ComplianceRegimesState } from "../models/mp-admin/compliance-regimes-state";
import { ConsumerBalancesState } from "../models/mp-admin/consumer-balances-state";
import { ConsumerDistributionKeysState } from "../models/mp-admin/consumer-distribution-keys-state";
import { ConsumerFundsState } from "../models/mp-admin/consumer-funds-state";
import { ConsumerProposalsState } from "../models/mp-admin/consumer-proposals-state";
import { AdminConsumerStatsState } from "../models/mp-admin/consumer-stats-state";
import { ConsumersState, ConsumerUsersState } from "../models/mp-admin/consumers-state";
import { ContractConsumerFundingDetailsState } from "../models/mp-admin/contract-consumer-funding-details-state";
import { DataRightsAdminState } from "../models/mp-admin/data-rights-admin-state";
import { ExcessProposalFundsState } from "../models/mp-admin/excess-proposal-funds-state";
import { FamilyAttributesState } from "../models/mp-admin/family-attributes-state";
import { InvoicesState } from "../models/mp-admin/invoices-state";
import { MarketplaceBalanceState } from "../models/mp-admin/marketplace-balance-state";
import { MonitoringRegimeDefaultsState } from "../models/mp-admin/monitoring-regime-defaults-state";
import { MonitoringTiersState } from "../models/mp-admin/monitoring-tiers-state";
import { ProductAttributesAdminState } from "../models/mp-admin/product-attributes-state";
import { ProductCategoriesState } from "../models/mp-admin/product-categories-state";
import { ProductFamiliesState } from "../models/mp-admin/product-families-state";
import { ProductStandardListsState } from "../models/mp-admin/product-standard-lists-state";
import { ProductsState } from "../models/mp-admin/products-state";
import { ProposalSummariesState } from "../models/mp-admin/proposal-summaries-state";
import { AdminVendorStatsState, VendorUsersState } from "../models/mp-admin/vendor-stats-state";
import { ActiveVendorsState } from "../models/mp-common/active-vendors-state";
import { CountryState } from "../models/mp-common/country-state";
import { CurrencyState } from "../models/mp-common/currency-state";
import { DataRightsState } from "../models/mp-common/data-rights-state";
import { DistributionKeyState } from "../models/mp-common/distribution-key-state";
import { LaunchSiteState } from "../models/mp-common/launch-site.state";
import { OnOrbitCountryState } from "../models/mp-common/on-orbit-country.state";
import { ProductTreeState } from "../models/mp-common/product-tree.state";
import { RfPolarizationState } from "../models/mp-common/rf-polarization.state";
import { RfSignalsState } from "../models/mp-common/rf-signals.state";
import { TagsState } from "../models/mp-common/tags-state";
import { VendorsState } from "../models/mp-common/vendors-state";
import { OnOrbitState } from "../models/on-orbit/on-orbit.state";
import { OnOrbitOrderState } from "../models/on-orbit/on-orbit-order-state";
import { VendorRatingState } from "../models/open/vendor-rating-state";
import { VendorReviewState } from "../models/open/vendor-review-state";
import { OrderProposalState } from "../models/order-proposal/order-proposal-state";
import { ProposalsByProductState } from "../models/proposal-by-product.state";
import { ProposalsState } from "../models/proposals-state";
import { PurchaseOrdersState } from "../models/purchase-orders-state";
import { RouterUrlState } from "../models/router-url-state";
import { ActionItemsState } from "../models/shared/action-items-state";
import { DailyStatsState } from "../models/shared/daily-stats-state";
import { DataTransactionsState } from "../models/shared/data-transactions-state";
import { StatState } from "../models/stat-state";
import { UserPreferencesState } from "../models/user-prefs/user-prefs-state";
import { UserState } from "../models/user-state";
import { UserStatusState } from "../models/user-status-state";
import { UsersState } from "../models/users-state";
import { VendorRolesState } from "../models/vendor-admin/vendor-roles.state";
import { VendorState } from "../models/vendor-state";
import { VendorSupportedProductsState } from "../models/vendor-supported-products-state";
import { StoreKeys } from "../store-keys";
import { apiClientReducer } from "./api-access/api-access.reducer";
import { businessClassificationReducer } from "./business-classification.reducer";
import { consumerReducer } from "./consumer.reducer";
import { contractConsumerFundingDetailsReducer } from "./consumer/contract-consumer-funding-details.reducer";
import { dataPerProductReducer } from "./consumer/data-per-product.reducer";
import { fundingBalanceReducer } from "./consumer/funding-balance.reducer";
import { orderCostStatsReducer } from "./consumer/order-cost-stats.reducer";
import { proposalFinancialReducer } from "./consumer/proposal-funding.reducer";
import { vendorGroupsReducer } from "./consumer/vendor-groups.reducer";
import { getActiveVendorOwnersReducer } from "./consumer-admin/get-active-vendor-owners.reducer";
import { getConsumerRolesReducer } from "./consumer-admin/get-consumer-roles.reducer";
import { consumerCountReducer } from "./consumer-count.reducer";
import { achBoStatusReducer } from "./consumer-transaction/ach-bo-status.reducer";
import { achDocumentReducer } from "./consumer-transaction/ach-document.reducer";
import { achStatusReducer } from "./consumer-transaction/ach-status.reducer";
import { dailyStatsReducer } from "./daily-stats.reducer";
import { dataTransactionsReducer } from "./data-transaction.reducer";
import { hrrReducer } from "./hrr/hrr.reducer";
import { conversationReducer } from "./messaging/conversation.reducer";
import { archivedMessageReducer, messageReducer } from "./messaging/message.reducer";
import { accountRequestsReducer } from "./mp-admin/account-requests.reducer";
import { activityCountsReducer } from "./mp-admin/activity-count.reducer";
import { adminDailyStatsReducer } from "./mp-admin/admin-daily-stats.reducer";
import { adminStatsReducer } from "./mp-admin/admin-stats.reducer";
import { AllVendorsReducer } from "./mp-admin/all-vendors.reducer";
import { analyticsExclusionReducer } from "./mp-admin/analytics-exclusion.reducer";
import { billableProposalsReducer } from "./mp-admin/billable-proposals-reducer";
import { complianceRegimesReducer } from "./mp-admin/compliance-regimes.reducer";
import { consumerBalancesReducer } from "./mp-admin/consumer-balances.reducer";
import { consumerDistributionKeysReducer } from "./mp-admin/consumer-distribution-keys.reducer";
import { consumerFundsReducer } from "./mp-admin/consumer-funds.reducer";
import { consumerProposalsReducer } from "./mp-admin/consumer-proposals.reducer";
import { consumerStatsReducer } from "./mp-admin/consumer-stats.reducer";
import { ConsumerUsersReducer } from "./mp-admin/consumer-users.reducer";
import { ConsumersReducer } from "./mp-admin/consumers.reducer";
import { dataRightsAdminReducer } from "./mp-admin/data-rights-admin.reducer";
import { excessFundsReducer } from "./mp-admin/excess-funds.reducer";
import { familyAttributesReducer } from "./mp-admin/family-attributes.reducer";
import { invoicesReducer } from "./mp-admin/invoices-reducer";
import { marketplaceBalanceReducer } from "./mp-admin/marketplace-balance.reducer";
import { monitoringRegimeDefaultsReducer } from "./mp-admin/monitoring-regime-defaults.reducer";
import { monitoringTiersReducer } from "./mp-admin/monitoring-tiers.reducer";
import { productAttributesAdminReducer } from "./mp-admin/product-attributes-admin.reducer";
import { productCategoriesReducer } from "./mp-admin/product-categories.reducer";
import { productFamiliesReducer } from "./mp-admin/product-families.reducer";
import { productStandardListsReducer } from "./mp-admin/product-standard-lists.reducer";
import { productsReducer } from "./mp-admin/products.reducer";
import { proposalSummariesReducer } from "./mp-admin/proposal-summaries.reducer";
import { vendorStatsReducer } from "./mp-admin/vendor-stats.reducer";
import { VendorUsersReducer } from "./mp-admin/vendor-users.reducer";
import { actionItemsReducer } from "./mp-common/action-items.reducer";
import { activeVendorsReducer } from "./mp-common/active-vendors-reducer";
import { countryReducer } from "./mp-common/country.reducer";
import { currencyReducer } from "./mp-common/currency.reducer";
import { dataRightsReducer } from "./mp-common/data-rights.reducer";
import { distributionKeyReducer } from "./mp-common/distribution-key.reducer";
import { launchSiteReducer } from "./mp-common/launch-site.reducer";
import { onOrbitCountryReducer } from "./mp-common/on-orbit-country.reducer";
import { rfPolarizationReducer } from "./mp-common/rf-polarization.reducer";
import { rfSignalsReducer } from "./mp-common/rf-signals.reducer";
import { tagsReducer } from "./mp-common/tags.reducer";
import { vendorsReducer } from "./mp-common/vendors-reducer";
import { onOrbitReducer } from "./on-orbit/on-orbit.reducer";
import { onOrbitOrderReducer } from "./on-orbit/on-orbit-order.reducer";
import { vendorRatingReducer } from "./open/vendor-rating.reducer";
import { vendorReviewReducer } from "./open/vendor-reviews.reducer";
import { orderProposalReducer } from "./order-proposal/order-proposal.reducer";
import { productTreeReducer } from "./product-tree.reducer";
import { proposalReducer } from "./proposal.reducer";
import { proposalsByProductReducer } from "./proposal-by-product.reducer";
import { purchaseOrderReducer } from "./purchase-order.reducer";
import { statReducer } from "./stats.reducer";
import { userReducer } from "./user.reducer";
import { userPreferencesReducer } from "./user-prefs/user-prefs.reducer";
import { userStatusReducer } from "./user-status.reducer";
import { usersReducer } from "./users.reducer";
import { vendorReducer } from "./vendor.reducer";
import { getVendorRolesReducer } from "./vendor-admin/get-vendor-roles.reducer";
import { vendorCountReducer } from "./vendor-count.reducer";
import { vendorSupportedProductsReducer } from "./vendor-supported-products.reducer";

export interface AppState {
	[StoreKeys.achBoStatusStateKey]?: AchBoStatusState;
	[StoreKeys.achDocumentStateKey]?: AchDocumentState;
	[StoreKeys.achStatusStateKey]?: AchStatusState;
	[StoreKeys.actionItemsStateKey]?: ActionItemsState;
	[StoreKeys.activeVendorsStateKey]?: ActiveVendorsState;
	[StoreKeys.activityCountsStateKey]?: ActivityCountState;
	[StoreKeys.adminStats]?: AdminStatsState;
	[StoreKeys.adminDailyStats]?: AdminDailyStatsState;
	[StoreKeys.analyticsExclusionStateKey]?: AnalyticsExclusionState;
	[StoreKeys.apiAccessStateKey]?: ApiClientState;
	[StoreKeys.archivedMessageStateKey]?: MessageState;
	[StoreKeys.businessClassificationStateKey]?: BusinessClassificationState;
	[StoreKeys.accountRequestsStateKey]?: AccountRequestsState;
	[StoreKeys.consumerBalancesStateKey]?: ConsumerBalancesState;
	[StoreKeys.consumerCountStateKey]?: CountState;
	[StoreKeys.consumerFundsStateKey]?: ConsumerFundsState;
	[StoreKeys.consumerProposalsStateKey]?: ConsumerProposalsState;
	[StoreKeys.contractConsumerFundingDetailsStateKey]?: ContractConsumerFundingDetailsState;
	[StoreKeys.consumerRoles]?: ConsumerRolesState;
	[StoreKeys.consumersStateKey]?: ConsumersState;
	[StoreKeys.consumerStateKey]?: ConsumerState;
	[StoreKeys.consumerUsersStateKey]?: ConsumerUsersState;
	[StoreKeys.consumerStatsStateKey]?: AdminConsumerStatsState;
	[StoreKeys.messageStateKey]?: MessageState;
	[StoreKeys.countryStateKey]?: CountryState;
	[StoreKeys.distributionKeyStateKey]?: DistributionKeyState;
	[StoreKeys.currencyStateKey]?: CurrencyState;
	[StoreKeys.dailyStatsStateKey]?: DailyStatsState;
	[StoreKeys.dataPerProductStatsStateKey]?: DataPerProductState;
	[StoreKeys.dataRightsStateKey]?: DataRightsState;
	[StoreKeys.dataTransactionsStateKey]?: DataTransactionsState;
	[StoreKeys.excessFundsStateKey]?: ExcessProposalFundsState;
	[StoreKeys.fundingBalanceStateKey]?: FundingBalanceState;
	[StoreKeys.getActiveVendorOwnersStateKey]?: GetActiveVendorOwnersState;
	[StoreKeys.marketplaceBalanceStateKey]?: MarketplaceBalanceState;
	[StoreKeys.conversationStateKey]?: ConversationState;
	[StoreKeys.onOrbitCountryStateKey]?: OnOrbitCountryState;
	[StoreKeys.onOrbitStateKey]?: OnOrbitState;
	[StoreKeys.hrrStateKey]?: HrrState;
	[StoreKeys.onOrbitOrderStateKey]?: OnOrbitOrderState;
	[StoreKeys.orderCostStatsStateKey]?: OrderCostStatsState;
	[StoreKeys.allVendorsStateKey]?: VendorsState;
	[StoreKeys.orderProposalStateKey]?: OrderProposalState;
	[StoreKeys.productTreeKey]?: ProductTreeState;
	[StoreKeys.launchSiteStateKey]?: LaunchSiteState;
	[StoreKeys.proposalFinancialsStateKey]?: ProposalsState;
	[StoreKeys.proposalStateKey]?: ProposalsState;
	[StoreKeys.proposalSummariesStateKey]?: ProposalSummariesState;
	[StoreKeys.proposalsByProductStateKey]?: ProposalsByProductState;
	[StoreKeys.rfPolarizationKey]?: RfPolarizationState;
	[StoreKeys.rfSignalsKey]?: RfSignalsState;
	[StoreKeys.routerStateKey]?: RouterReducerState<RouterUrlState>;
	[StoreKeys.statStateKey]?: StatState;
	[StoreKeys.userStateKey]?: UserState;
	[StoreKeys.usersStateKey]?: UsersState;
	[StoreKeys.userStatusStateKey]?: UserStatusState;
	[StoreKeys.vendorCountStateKey]?: CountState;
	[StoreKeys.vendorRatingStateKey]?: VendorRatingState;
	[StoreKeys.vendorReviewStateKey]?: VendorReviewState;
	[StoreKeys.vendorRolesStateKey]?: VendorRolesState;
	[StoreKeys.vendorsStateKey]?: VendorsState;
	[StoreKeys.vendorStateKey]?: VendorState;
	[StoreKeys.vendorStatsStateKey]?: AdminVendorStatsState;
	[StoreKeys.vendorUsersStateKey]?: VendorUsersState;
	[StoreKeys.userPrefsKey]?: UserPreferencesState;
	[StoreKeys.tagsKey]?: TagsState;
	[StoreKeys.vendorGroupsKey]?: VendorGroupsState;
	[StoreKeys.billableProposalsStateKey]?: BillableProposalsState;
	[StoreKeys.invoicesStateKey]?: InvoicesState;
	[StoreKeys.productFamiliesStateKey]?: ProductFamiliesState;
	[StoreKeys.productCategoriesStateKey]?: ProductCategoriesState;
	[StoreKeys.productsStateKey]?: ProductsState;
	[StoreKeys.familyAttributesStateKey]?: FamilyAttributesState;
	[StoreKeys.productAttributesAdminStateKey]?: ProductAttributesAdminState;
	[StoreKeys.monitoringTiersStateKey]?: MonitoringTiersState;
	[StoreKeys.monitoringRegimeDefaultsStateKey]?: MonitoringRegimeDefaultsState;
	[StoreKeys.productStandardListsStateKey]?: ProductStandardListsState;
	[StoreKeys.purchaseOrdersStateKey]?: PurchaseOrdersState;
	[StoreKeys.dataRightsAdminStateKey]?: DataRightsAdminState;
	[StoreKeys.consumerDistributionKeysStateKey]?: ConsumerDistributionKeysState;
	[StoreKeys.vendorSupportedProductsStateKey]?: VendorSupportedProductsState;
	[StoreKeys.complianceRegimesStateKey]?: ComplianceRegimesState;
}

export const reducers: ActionReducerMap<AppState> = {
	[StoreKeys.achBoStatusStateKey]: achBoStatusReducer,
	[StoreKeys.achDocumentStateKey]: achDocumentReducer,
	[StoreKeys.achStatusStateKey]: achStatusReducer,
	[StoreKeys.actionItemsStateKey]: actionItemsReducer,
	[StoreKeys.activeVendorsStateKey]: activeVendorsReducer,
	[StoreKeys.activityCountsStateKey]: activityCountsReducer,
	[StoreKeys.adminStats]: adminStatsReducer,
	[StoreKeys.adminDailyStats]: adminDailyStatsReducer,
	[StoreKeys.analyticsExclusionStateKey]: analyticsExclusionReducer,
	[StoreKeys.apiAccessStateKey]: apiClientReducer,
	[StoreKeys.archivedMessageStateKey]: archivedMessageReducer,
	[StoreKeys.consumersStateKey]: ConsumersReducer,
	[StoreKeys.allVendorsStateKey]: AllVendorsReducer,
	[StoreKeys.consumerUsersStateKey]: ConsumerUsersReducer,
	[StoreKeys.vendorUsersStateKey]: VendorUsersReducer,
	[StoreKeys.businessClassificationStateKey]: businessClassificationReducer,
	[StoreKeys.accountRequestsStateKey]: accountRequestsReducer,
	[StoreKeys.consumerBalancesStateKey]: consumerBalancesReducer,
	[StoreKeys.consumerCountStateKey]: consumerCountReducer,
	[StoreKeys.consumerFundsStateKey]: consumerFundsReducer,
	[StoreKeys.consumerProposalsStateKey]: consumerProposalsReducer,
	[StoreKeys.contractConsumerFundingDetailsStateKey]: contractConsumerFundingDetailsReducer,
	[StoreKeys.consumerStateKey]: consumerReducer,
	[StoreKeys.consumerStatsStateKey]: consumerStatsReducer,
	[StoreKeys.conversationStateKey]: conversationReducer,
	[StoreKeys.countryStateKey]: countryReducer,
	[StoreKeys.distributionKeyStateKey]: distributionKeyReducer,
	[StoreKeys.currencyStateKey]: currencyReducer,
	[StoreKeys.dailyStatsStateKey]: dailyStatsReducer,
	[StoreKeys.dataPerProductStatsStateKey]: dataPerProductReducer,
	[StoreKeys.dataRightsStateKey]: dataRightsReducer,
	[StoreKeys.dataTransactionsStateKey]: dataTransactionsReducer,
	[StoreKeys.excessFundsStateKey]: excessFundsReducer,
	[StoreKeys.fundingBalanceStateKey]: fundingBalanceReducer,
	[StoreKeys.getActiveVendorOwnersStateKey]: getActiveVendorOwnersReducer,
	[StoreKeys.consumerRoles]: getConsumerRolesReducer,
	[StoreKeys.vendorRolesStateKey]: getVendorRolesReducer,
	[StoreKeys.marketplaceBalanceStateKey]: marketplaceBalanceReducer,
	[StoreKeys.messageStateKey]: messageReducer,
	[StoreKeys.onOrbitCountryStateKey]: onOrbitCountryReducer,
	[StoreKeys.launchSiteStateKey]: launchSiteReducer,
	[StoreKeys.onOrbitStateKey]: onOrbitReducer,
	[StoreKeys.hrrStateKey]: hrrReducer,
	[StoreKeys.onOrbitOrderStateKey]: onOrbitOrderReducer,
	[StoreKeys.orderCostStatsStateKey]: orderCostStatsReducer,
	[StoreKeys.orderProposalStateKey]: orderProposalReducer,
	[StoreKeys.productTreeKey]: productTreeReducer,
	[StoreKeys.proposalFinancialsStateKey]: proposalFinancialReducer,
	[StoreKeys.proposalStateKey]: proposalReducer,
	[StoreKeys.proposalSummariesStateKey]: proposalSummariesReducer,
	[StoreKeys.proposalsByProductStateKey]: proposalsByProductReducer,
	[StoreKeys.rfPolarizationKey]: rfPolarizationReducer,
	[StoreKeys.rfSignalsKey]: rfSignalsReducer,
	[StoreKeys.routerStateKey]: routerReducer,
	[StoreKeys.statStateKey]: statReducer,
	[StoreKeys.userStateKey]: userReducer,
	[StoreKeys.usersStateKey]: usersReducer,
	[StoreKeys.userStatusStateKey]: userStatusReducer,
	[StoreKeys.vendorCountStateKey]: vendorCountReducer,
	[StoreKeys.vendorRatingStateKey]: vendorRatingReducer,
	[StoreKeys.vendorReviewStateKey]: vendorReviewReducer,
	[StoreKeys.vendorsStateKey]: vendorsReducer,
	[StoreKeys.vendorStateKey]: vendorReducer,
	[StoreKeys.vendorStatsStateKey]: vendorStatsReducer,
	[StoreKeys.userPrefsKey]: userPreferencesReducer,
	[StoreKeys.tagsKey]: tagsReducer,
	[StoreKeys.vendorGroupsKey]: vendorGroupsReducer,
	[StoreKeys.billableProposalsStateKey]: billableProposalsReducer,
	[StoreKeys.invoicesStateKey]: invoicesReducer,
	[StoreKeys.productFamiliesStateKey]: productFamiliesReducer,
	[StoreKeys.productCategoriesStateKey]: productCategoriesReducer,
	[StoreKeys.productsStateKey]: productsReducer,
	[StoreKeys.familyAttributesStateKey]: familyAttributesReducer,
	[StoreKeys.productAttributesAdminStateKey]: productAttributesAdminReducer,
	[StoreKeys.monitoringTiersStateKey]: monitoringTiersReducer,
	[StoreKeys.monitoringRegimeDefaultsStateKey]: monitoringRegimeDefaultsReducer,
	[StoreKeys.productStandardListsStateKey]: productStandardListsReducer,
	[StoreKeys.purchaseOrdersStateKey]: purchaseOrderReducer,
	[StoreKeys.dataRightsAdminStateKey]: dataRightsAdminReducer,
	[StoreKeys.consumerDistributionKeysStateKey]: consumerDistributionKeysReducer,
	[StoreKeys.vendorSupportedProductsStateKey]: vendorSupportedProductsReducer,
	[StoreKeys.complianceRegimesStateKey]: complianceRegimesReducer
};

export function clearState(reducer) {
	return (state, action) => {
		return action.type === LogoutRequested.type
			? reducer(undefined, action)
			: reducer(state, action);
	};
}

export const REDUCERS_TOKEN = new InjectionToken<ActionReducerMap<AppState>>("App Reducers");
export const metaReducers: MetaReducer<AppState>[] = [clearState];
